/**
 * Gzip compression utilities for CRDT payloads.
 *
 * Compress before encryption (encrypted data is incompressible).
 * Yjs binary updates compress ~5x with gzip due to repeated structure.
 *
 * Backward compatible: decompress detects gzip magic bytes (0x1f 0x8b)
 * and passes through uncompressed data unchanged.
 */

const GZIP_MAGIC_0 = 0x1f;
const GZIP_MAGIC_1 = 0x8b;

// Max decompressed size: 16 MB. Prevents decompression bombs from
// malicious participants in shared documents.
const MAX_DECOMPRESSED_SIZE = 16 * 1024 * 1024;

/**
 * Gzip-compress a Uint8Array.
 * @param {Uint8Array} data
 * @returns {Promise<Uint8Array>}
 */
export async function compress(data) {
  const cs = new CompressionStream("gzip");
  const writer = cs.writable.getWriter();
  writer.write(data);
  writer.close();
  return new Uint8Array(await new Response(cs.readable).arrayBuffer());
}

/**
 * Decompress a Uint8Array if gzip-compressed, otherwise return as-is.
 * Falls back to returning raw data if decompression fails (handles the
 * unlikely case where Yjs binary coincidentally starts with gzip magic).
 * @param {Uint8Array} data
 * @returns {Promise<Uint8Array>}
 */
export async function decompress(data) {
  if (!isGzipped(data)) return data;
  let result;
  try {
    const ds = new DecompressionStream("gzip");
    const writer = ds.writable.getWriter();
    writer.write(data);
    writer.close();
    result = new Uint8Array(await new Response(ds.readable).arrayBuffer());
  } catch {
    // Not valid gzip despite magic bytes — return as-is
    return data;
  }
  if (result.byteLength > MAX_DECOMPRESSED_SIZE) {
    throw new Error(`Decompressed size ${result.byteLength} exceeds limit ${MAX_DECOMPRESSED_SIZE}`);
  }
  return result;
}

/**
 * Check if data starts with gzip magic bytes.
 * @param {Uint8Array} data
 * @returns {boolean}
 */
export function isGzipped(data) {
  return data.length >= 2 && data[0] === GZIP_MAGIC_0 && data[1] === GZIP_MAGIC_1;
}
